import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Snackbar,
  Tooltip,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import QRCode from 'react-qr-code';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ShareIcon from '@mui/icons-material/Share';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QuizIcon from '@mui/icons-material/Quiz';
import PendingIcon from '@mui/icons-material/Pending';
import BlockIcon from '@mui/icons-material/Block';
import PauseIcon from '@mui/icons-material/Pause';
import CrownIcon from '@mui/icons-material/WorkspacePremium';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LoginIcon from '@mui/icons-material/Login';
import LockIcon from '@mui/icons-material/Lock';
import Login from 'components/pages/login'
import { 
  JoinFab, 
  ShareButton,
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  StyledAlert,
  QRCodeContainer,
  QRCodePlaceholder, 
  SectionHeader,
  ModernGrid,
  MembershipStatusContainer,
  StatusBadge,
  RoleChip,
  StatusTooltipContent
} from './styled';

export const JoinCommunityFabView = ({ 
  community, 
  onJoinRequest, 
  onSubmitAdmissionAnswers, 
  my_membership 
}) => {
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [joinStep, setJoinStep] = useState('confirm'); // 'confirm', 'auth', 'questions', 'success'
  
  // Check if user is logged in (use state so it updates after login)
  const [isLoggedIn, setIsLoggedIn] = useState(
    localStorage.getItem('token') ? true : false
  );
  
  // Join response data
  const [joinResponse, setJoinResponse] = useState(null);
  const [admissionAnswers, setAdmissionAnswers] = useState({});
  const [questionErrors, setQuestionErrors] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Helper functions for status display
  const getStatusInfo = (status) => {
    switch (status) {
      case 'AC':
        return {
          label: 'Active',
          icon: <CheckCircleIcon />,
          description: 'You are an active member of this community',
          color: 'success'
        };
      case 'PP':
        return {
          label: 'Pending Review',
          icon: <PendingIcon />,
          description: 'Your application is being reviewed by community administrators',
          color: 'warning'
        };
      case 'RE':
        return {
          label: 'Application Rejected',
          icon: <BlockIcon />,
          description: 'Your application was not approved. Contact administrators for more info.',
          color: 'error'
        };
      case 'IN':
        return {
          label: 'Inactive',
          icon: <PauseIcon />,
          description: 'Your membership is currently inactive',
          color: 'info'
        };
      case 'BA':
        return {
          label: 'Banned',
          icon: <BlockIcon />,
          description: 'You have been banned from this community',
          color: 'error'
        };
      default:
        return {
          label: 'Unknown Status',
          icon: <PersonIcon />,
          description: 'Contact administrators for status information',
          color: 'default'
        };
    }
  };

  const getRoleInfo = (role) => {
    switch (role) {
      case 'FO':
        return { label: 'Founder', icon: <CrownIcon /> };
      case 'AD':
        return { label: 'Admin', icon: <AdminPanelSettingsIcon /> };
      case 'MA':
        return { label: 'Manager', icon: <ManageAccountsIcon /> };
      case 'PA':
        return { label: 'Participant', icon: <PersonIcon /> };
      case 'GU':
        return { label: 'Guest', icon: <VisibilityIcon /> };
      default:
        return { label: 'Member', icon: <PersonIcon /> };
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderMembershipStatus = () => {
    if (!my_membership) return null;

    const statusInfo = getStatusInfo(my_membership.status);
    const roleInfo = getRoleInfo(my_membership.role);

    const tooltipContent = (
      <StatusTooltipContent>
        <Box className="status-header">
          {statusInfo.icon}
          <Typography variant="subtitle2">
            {statusInfo.label}
          </Typography>
        </Box>
        <Typography className="status-details">
          {statusInfo.description}
        </Typography>
        <Typography className="status-date">
          Member since {formatDate(my_membership.create_date)}
        </Typography>
        {my_membership.status === 'PP' && (
          <Typography className="status-date">
            Application submitted {formatDate(my_membership.create_date)}
          </Typography>
        )}
      </StatusTooltipContent>
    );

    return (
      <MembershipStatusContainer>
        <Tooltip title={tooltipContent} arrow placement="top">
          <StatusBadge 
            status={my_membership.status}
            role={my_membership.role}
            variant="extended"
          >
            {statusInfo.icon}
            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1, ml: 0.5 }}>
              {statusInfo.label}
            </Typography>
          </StatusBadge>
        </Tooltip>
        
        {(my_membership.role !== 'PA' && my_membership.role !== 'GU') && (
          <RoleChip
            role={my_membership.role}
            icon={roleInfo.icon}
            label={roleInfo.label}
            size="small"
          />
        )}
      </MembershipStatusContainer>
    );
  };

  const handleLoginRedirect = () => {
    // Instead of redirecting, show the auth step
    setJoinStep('auth');
  };

  const handleLoginSuccess = (loginResult) => {
    // Update logged in status
    setIsLoggedIn(true);
    
    // Check if community allows direct membership
    const isAutoApprove = community?.default_status === 'AC';
    
    if (isAutoApprove) {
      // Auto-trigger join for auto-approve communities
      setJoinStep('confirm');
      // Small delay to let user see they're logged in
      setTimeout(() => {
        handleJoinSubmit();
      }, 500);
    } else {
      // Go back to confirmation step for approval-required communities
      setJoinStep('confirm');
    }
  };

  const handleAuthClose = () => {
    // User closed auth modal, go back to join confirmation
    setJoinStep('confirm');
  };

  const handleJoinSubmit = async () => {
    setSubmitLoading(true);
    
    try {
      const result = await onJoinRequest();
      
      if (result.success) {
        setJoinResponse(result.data);
        
        if (result.data.requires_questions && result.data.questions && result.data.questions.length > 0) {
          // Initialize answers object with existing answers if any
          const initialAnswers = {};
          result.data.questions.forEach(question => {
            if (question.current_answer) {
              const answer = question.current_answer;
              initialAnswers[question.id] = {
                text_answer: answer.text_answer || '',
                number_answer: answer.number_answer || '',
                boolean_answer: answer.boolean_answer,
                choice_answers: answer.choice_answers || []
              };
            } else {
              // Initialize empty answers based on question type
              initialAnswers[question.id] = {
                text_answer: '',
                number_answer: '',
                boolean_answer: null,
                choice_answers: []
              };
            }
          });
          setAdmissionAnswers(initialAnswers);
          setJoinStep('questions');
        } else {
          setJoinStep('success');
        }
      } else {
        console.error('Join failed:', result.error);
        // Handle error - you might want to show an error message
      }
    } catch (error) {
      console.error('Join request failed:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answerType, value) => {
    setAdmissionAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [answerType]: value
      }
    }));
    
    // Clear error for this question
    if (questionErrors[questionId]) {
      setQuestionErrors(prev => ({
        ...prev,
        [questionId]: null
      }));
    }
  };

  const validateCurrentQuestion = (question) => {
    const answer = admissionAnswers[question.id];
    
    if (question.is_required) {
      let hasValue = false;
      
      switch (question.question_type) {
        case 'TX':
        case 'TA':
        case 'EM':
        case 'UR':
          hasValue = answer.text_answer && answer.text_answer.trim() !== '';
          break;
        case 'NU':
          hasValue = answer.number_answer !== '' && answer.number_answer !== null;
          break;
        case 'BO':
          hasValue = answer.boolean_answer !== null;
          break;
        case 'SC':
        case 'MC':
          hasValue = answer.choice_answers && answer.choice_answers.length > 0;
          break;
      }
      
      if (!hasValue) {
        setQuestionErrors(prev => ({
          ...prev,
          [question.id]: 'This question is required'
        }));
        return false;
      }
    }
    
    return true;
  };

  const handleNextQuestion = () => {
    const currentQuestion = joinResponse.questions[currentQuestionIndex];
    if (validateCurrentQuestion(currentQuestion)) {
      if (currentQuestionIndex < joinResponse.questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        handleSubmitAnswers();
      }
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmitAnswers = async () => {
    setSubmitLoading(true);
    
    try {
      // Format answers for API
      const formattedAnswers = joinResponse.questions.map(question => {
        const answer = admissionAnswers[question.id];
        return {
          question_id: question.id,
          text_answer: answer.text_answer || null,
          number_answer: answer.number_answer || null,
          boolean_answer: answer.boolean_answer,
          choice_answers: answer.choice_answers && answer.choice_answers.length > 0 ? answer.choice_answers : null
        };
      });

      if (onSubmitAdmissionAnswers) {
        const result = await onSubmitAdmissionAnswers(community.slug, formattedAnswers);
        if (result.success) {
          // Update joinResponse with the submission result
          setJoinResponse(prev => ({
            ...prev,
            ...result,
            // Keep the original membership data but update status message
            status_message: result.status_message,
            completion_percentage: result.completion_percentage,
            is_complete: result.is_complete
          }));
          setJoinStep('success');
        } else {
          console.error('Failed to submit answers:', result);
        }
      }
    } catch (error) {
      console.error('Submit answers failed:', error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderQuestionInput = (question) => {
    const answer = admissionAnswers[question.id];
    const error = questionErrors[question.id];

    switch (question.question_type) {
      case 'TX':
        return (
          <TextField
            fullWidth
            label={question.question_text}
            value={answer.text_answer}
            onChange={(e) => handleAnswerChange(question.id, 'text_answer', e.target.value)}
            required={question.is_required}
            error={!!error}
            helperText={error}
            inputProps={{ maxLength: question.max_length }}
          />
        );

      case 'TA':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            label={question.question_text}
            value={answer.text_answer}
            onChange={(e) => handleAnswerChange(question.id, 'text_answer', e.target.value)}
            required={question.is_required}
            error={!!error}
            helperText={error}
            inputProps={{ maxLength: question.max_length }}
          />
        );

      case 'EM':
        return (
          <TextField
            fullWidth
            type="email"
            label={question.question_text}
            value={answer.text_answer}
            onChange={(e) => handleAnswerChange(question.id, 'text_answer', e.target.value)}
            required={question.is_required}
            error={!!error}
            helperText={error}
          />
        );

      case 'UR':
        return (
          <TextField
            fullWidth
            type="url"
            label={question.question_text}
            value={answer.text_answer}
            onChange={(e) => handleAnswerChange(question.id, 'text_answer', e.target.value)}
            required={question.is_required}
            error={!!error}
            helperText={error}
          />
        );

      case 'NU':
        return (
          <TextField
            fullWidth
            type="number"
            label={question.question_text}
            value={answer.number_answer}
            onChange={(e) => handleAnswerChange(question.id, 'number_answer', e.target.value)}
            required={question.is_required}
            error={!!error}
            helperText={error}
            inputProps={{
              min: question.min_value,
              max: question.max_value
            }}
          />
        );

      case 'BO':
        return (
          <FormControl component="fieldset" error={!!error}>
            <FormLabel component="legend">{question.question_text}</FormLabel>
            <RadioGroup
              value={answer.boolean_answer === null ? '' : answer.boolean_answer.toString()}
              onChange={(e) => handleAnswerChange(question.id, 'boolean_answer', e.target.value === 'true')}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
            {error && <Typography color="error" variant="caption">{error}</Typography>}
          </FormControl>
        );

      case 'SC':
        return (
          <FormControl component="fieldset" error={!!error}>
            <FormLabel component="legend">{question.question_text}</FormLabel>
            <RadioGroup
              value={answer.choice_answers[0] || ''}
              onChange={(e) => handleAnswerChange(question.id, 'choice_answers', [e.target.value])}
            >
              {question.choices && question.choices.map((choice, index) => (
                <FormControlLabel 
                  key={index} 
                  value={choice} 
                  control={<Radio />} 
                  label={choice} 
                />
              ))}
            </RadioGroup>
            {error && <Typography color="error" variant="caption">{error}</Typography>}
          </FormControl>
        );

      case 'MC':
        return (
          <FormControl component="fieldset" error={!!error}>
            <FormLabel component="legend">{question.question_text}</FormLabel>
            <FormGroup>
              {question.choices && question.choices.map((choice, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={answer.choice_answers.includes(choice)}
                      onChange={(e) => {
                        const newChoices = e.target.checked
                          ? [...answer.choice_answers, choice]
                          : answer.choice_answers.filter(c => c !== choice);
                        handleAnswerChange(question.id, 'choice_answers', newChoices);
                      }}
                    />
                  }
                  label={choice}
                />
              ))}
            </FormGroup>
            {error && <Typography color="error" variant="caption">{error}</Typography>}
          </FormControl>
        );

      default:
        return null;
    }
  };

  const handleCloseDialog = () => {
    setJoinDialogOpen(false);
    setJoinStep('confirm'); // Reset to confirm step
    setJoinResponse(null);
    setAdmissionAnswers({});
    setQuestionErrors({});
    setCurrentQuestionIndex(0);
  };

  const getStepContent = () => {
    // Show auth component if user is not logged in and on auth step
    if (!isLoggedIn && joinStep === 'auth') {
      return (
        <Login 
          isModal={true}
          onLoginSuccess={handleLoginSuccess}
          onClose={handleAuthClose}
        />
      );
    }

    // Show login required message if user is not logged in on initial step
    if (!isLoggedIn && joinStep === 'confirm') {
      return (
        <>
          {community?.image && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img 
                src={community.image} 
                alt={community.name}
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '16px',
                  objectFit: 'cover'
                }} 
              />
            </Box>
          )}
          
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <LockIcon sx={{ 
              fontSize: 64, 
              color: 'info.main', 
              mb: 2 
            }} />
            
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
              Login Required
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              You need to be logged in to join <strong>{community?.name}</strong>
            </Typography>

            <StyledAlert severity="info" sx={{ mb: 3 }}>
              🔐 Please log in to your account to join this community. If you don't have an account yet, you can create one during the login process.
            </StyledAlert>

            {community?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2, fontStyle: 'italic' }}>
                "{community.description}"
              </Typography>
            )}
          </Box>
        </>
      );
    }

    switch (joinStep) {
      case 'confirm':
        const isAutoApprove = community?.default_status === 'AC';
        const requiresQuestions = community?.requires_admission_questions;
        
        return (
          <>
            {community?.image && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <img 
                  src={community.image} 
                  alt={community.name}
                  style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '16px',
                    objectFit: 'cover'
                  }} 
                />
              </Box>
            )}
            
            <Typography variant="h6" sx={{ textAlign: 'center', mb: 2, fontWeight: 700 }}>
              Join {community?.name}
            </Typography>
            
            {community?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 3, fontStyle: 'italic' }}>
                "{community.description}"
              </Typography>
            )}

            <StyledAlert severity={isAutoApprove ? "success" : "info"} sx={{ mb: 3 }}>
              {isAutoApprove ? (
                requiresQuestions ? 
                  "🎉 You'll become an active member immediately! You may be asked some questions to help us know you better." :
                  "🎉 You'll become an active member immediately! Welcome aboard!"
              ) : (
                requiresQuestions ?
                  "📝 Your application will be reviewed by community administrators. You may need to answer some questions first." :
                  "⏳ Your application will be reviewed by community administrators. You'll receive an email confirmation once approved."
              )}
            </StyledAlert>

            {community?.contact_email && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
                Questions? Contact us at <strong>{community.contact_email}</strong>
              </Typography>
            )}

            <Box sx={{ 
              backgroundColor: 'rgba(241, 196, 15, 0.1)', 
              borderRadius: 2, 
              p: 2, 
              mt: 2,
              border: '1px solid rgba(241, 196, 15, 0.3)'
            }}>
              <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 600 }}>
                Ready to join our community?
              </Typography>
            </Box>
          </>
        );

      case 'questions':
        if (!joinResponse?.questions || joinResponse.questions.length === 0) return null;
        
        const currentQuestion = joinResponse.questions[currentQuestionIndex];
        const progress = ((currentQuestionIndex + 1) / joinResponse.questions.length) * 100;

        return (
          <>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QuizIcon />
                  Admission Questions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentQuestionIndex + 1} of {joinResponse.questions.length}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ mb: 2 }} />
            </Box>

            <Box sx={{ mb: 3 }}>
              {renderQuestionInput(currentQuestion)}
            </Box>
          </>
        );

      case 'success':
        const isActiveMember = joinResponse?.membership?.status === 'AC';
        const questionsCompleted = joinResponse?.is_complete || joinResponse?.completion_percentage === 100;
        
        return (
          <>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {community?.image && (
                <img 
                  src={community.image} 
                  alt={community.name}
                  style={{ 
                    width: '60px', 
                    height: '60px', 
                    borderRadius: '12px',
                    objectFit: 'cover',
                    marginBottom: '16px'
                  }} 
                />
              )}
              
              <CheckCircleIcon sx={{ 
                fontSize: 64, 
                color: questionsCompleted ? 'success.main' : 'info.main', 
                mb: 2 
              }} />
              
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                {questionsCompleted ? '✅ All Set!' : 
                 isActiveMember ? '🎉 Welcome to the Club!' : '📝 Application Submitted!'}
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                {joinResponse?.status_message || 
                 (isActiveMember ? "Welcome to the community!" : "Your application has been submitted for review.")}
              </Typography>
              
              {questionsCompleted ? (
                <Box sx={{ 
                  backgroundColor: 'rgba(102, 187, 106, 0.1)', 
                  borderRadius: 2, 
                  p: 2, 
                  mt: 2,
                  border: '1px solid rgba(102, 187, 106, 0.3)'
                }}>
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                    {isActiveMember ? 
                      `🎉 You're now an active member of ${community?.name}!` :
                      `✅ Questions completed! Your application is ready for review.`
                    }
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  backgroundColor: 'rgba(41, 182, 246, 0.1)', 
                  borderRadius: 2, 
                  p: 2, 
                  mt: 2,
                  border: '1px solid rgba(41, 182, 246, 0.3)'
                }}>
                  <Typography variant="body2" color="info.main" sx={{ fontWeight: 600 }}>
                    We'll review your application and get back to you soon at {community?.contact_email}
                  </Typography>
                </Box>
              )}

              {joinResponse?.completion_percentage !== undefined && joinResponse.completion_percentage < 100 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Questions completed: {joinResponse.completion_percentage}%
                </Typography>
              )}
            </Box>
          </>
        );

      default:
        return null;
    }
  };

  const getDialogActions = () => {
    // Hide actions when showing auth component
    if (!isLoggedIn && joinStep === 'auth') {
      return null;
    }

    // Show login button if user is not logged in on confirm step
    if (!isLoggedIn && joinStep === 'confirm') {
      return (
        <>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleLoginRedirect}
            startIcon={<LoginIcon />}
            sx={{ minWidth: '140px' }}
          >
            Login to Join
          </Button>
        </>
      );
    }

    switch (joinStep) {
      case 'confirm':
        return (
          <>
            <Button onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button 
              variant="contained"
              onClick={handleJoinSubmit}
              disabled={submitLoading}
              sx={{ minWidth: '140px' }}
            >
              {submitLoading ? 'Joining...' : 
               community?.default_status === 'AC' ? 'Join Now' : 'Submit Application'}
            </Button>
          </>
        );

      case 'questions':
        return (
          <>
            <Button 
              onClick={handlePrevQuestion}
              disabled={currentQuestionIndex === 0}
              startIcon={<ArrowBackIcon />}
            >
              Previous
            </Button>
            <Button 
              variant="contained"
              onClick={handleNextQuestion}
              disabled={submitLoading}
              endIcon={currentQuestionIndex === joinResponse.questions.length - 1 ? <CheckCircleIcon /> : <ArrowForwardIcon />}
            >
              {submitLoading ? 'Submitting...' : 
               currentQuestionIndex === joinResponse.questions.length - 1 ? 'Submit' : 'Next'}
            </Button>
          </>
        );

      case 'success':
        return (
          <Button 
            variant="contained"
            onClick={handleCloseDialog}
          >
            Close
          </Button>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Show status badge if user has membership, otherwise show join button */}
      {my_membership ? (
        renderMembershipStatus()
      ) : (
        <JoinFab
          onClick={() => setJoinDialogOpen(true)}
          variant="extended"
        >
          <PersonAddIcon />
          Join Community
        </JoinFab>
      )}

      <StyledDialog 
        open={joinDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isLoggedIn && joinStep === 'auth' ? <LoginIcon /> :
               !isLoggedIn ? <LockIcon /> : <PersonAddIcon />}
              <Typography variant="h6">
                {!isLoggedIn && joinStep === 'auth' ? 'Login to Continue' :
                 !isLoggedIn ? 'Login Required' :
                 joinStep === 'questions' ? 'Admission Questions' : 
                 joinStep === 'success' ? 'Application Submitted' :
                 `Join ${community?.name || 'Community'}`}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </StyledDialogTitle>
        
        <StyledDialogContent>
          {getStepContent()}
        </StyledDialogContent>
        
        <StyledDialogActions>
          {getDialogActions()}
        </StyledDialogActions>
      </StyledDialog>
    </>
  );
};

export const ShareCommunityButtonView = ({ community }) => {
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const communityUrl = `${window.location.origin}/community/${community?.slug || 'demo'}`;
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(communityUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <>
      <Tooltip title="Share Community">
        <ShareButton
          variant="outlined"
          startIcon={<ShareIcon />}
          onClick={() => setShareDialogOpen(true)}
        >
          Share
        </ShareButton>
      </Tooltip>

      <StyledDialog 
        open={shareDialogOpen} 
        onClose={() => setShareDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShareIcon />
              <Typography variant="h6">
                Share {community?.name || 'Community'}
              </Typography>
            </Box>
            <IconButton onClick={() => setShareDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </StyledDialogTitle>
        
        <StyledDialogContent>
          <ModernGrid>
            {/* QR Code Section */}
            <QRCodeContainer>
              <SectionHeader>
                <QrCodeIcon />
                <Typography variant="h6">
                  QR Code
                </Typography>
              </SectionHeader>
              
              <QRCodePlaceholder >
                <Box sx={{ textAlign: 'center' }}>
                  {/* Use react-qr-code */}
                  <Box sx={{ 
                    padding: 2, 
                    backgroundColor: 'white',
                    borderRadius: 2,
                    display: 'inline-block',
                    mb: 1
                  }}>
                    <QRCode 
                      id="qr-code-svg"
                      value={communityUrl}
                      size={140}
                      level="M"
                    />
                  </Box>
                </Box>
              </QRCodePlaceholder>
            </QRCodeContainer>

            {/* Share Link Section */}
            <QRCodeContainer>
              <SectionHeader>
                <LinkIcon />
                <Typography variant="h6">
                  Share Link
                </Typography>
              </SectionHeader>
              
              <QRCodePlaceholder onClick={handleCopyLink} sx={{ cursor: 'pointer' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <LinkIcon sx={{ 
                    fontSize: 48, 
                    mb: 1,
                    color: 'primary.main',
                    filter: 'drop-shadow(0 4px 8px rgba(241, 196, 15, 0.3))'
                  }} />
                  <Typography variant="body2" color="text.secondary">
                    Copy link for<br />{community?.name || 'Community'}
                  </Typography>
                </Box>
              </QRCodePlaceholder>
            </QRCodeContainer>
          </ModernGrid>
        </StyledDialogContent>
      </StyledDialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        message="Link copied to clipboard!"
        action={
          <IconButton size="small" color="inherit" onClick={() => setCopySuccess(false)}>
            <CheckCircleIcon fontSize="small" />
          </IconButton>
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};